/**
 * favorites.ts — избранное (localStorage + серверная синхронизация)
 * Проект: TorgiRT (shared — все витрины)
 *
 * Этап 1: localStorage для всех пользователей (гости и авторизованные).
 * Этап 2: Серверная синхронизация для авторизованных.
 *
 * Формат localStorage:
 *   Ключ "torgirt-{storefront}-favorites":
 *     [{"a":"ART-001","t":1709312400}, {"a":"ART-002","t":1709312500}]
 *   Обратная совместимость: ["ART-001", "ART-002"] → конвертируется при чтении.
 *
 *   Ключ "torgirt-{storefront}-favorites-sync":
 *     1709312600  (UNIX timestamp последней успешной синхронизации)
 */

import { getConfig } from './config';
import { showFlash, showToast } from './toast';

const _cfg = window.CART_CONFIG || {};
const _sf = _cfg.storefront || 'i';
const FAVORITES_KEY = "torgirt-" + _sf + "-favorites";
const SYNC_TS_KEY = "torgirt-" + _sf + "-favorites-sync";

/** Кешированный Map<article, timestamp> */
let _cache: Map<string, number> | null = null;

function getFavoritesMap(): Map<string, number> {
  if (_cache) return _cache;
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) {
      _cache = new Map();
      return _cache;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      _cache = new Map();
      return _cache;
    }
    _cache = new Map();
    for (const item of parsed) {
      if (typeof item === 'string') {
        if (item) _cache.set(item, 0);
      } else if (item && typeof item === 'object' && item.a) {
        _cache.set(String(item.a), Number(item.t) || 0);
      }
    }
  } catch {
    _cache = new Map();
  }
  return _cache;
}

function saveFavorites(map: Map<string, number>): void {
  try {
    const arr: { a: string; t: number }[] = [];
    for (const [a, t] of map) {
      arr.push({ a, t });
    }
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(arr));
    _cache = map;
  } catch {
    // localStorage переполнен — игнорируем
  }
}

function getLastSyncAt(): number {
  try {
    return Number(localStorage.getItem(SYNC_TS_KEY)) || 0;
  } catch {
    return 0;
  }
}

function setLastSyncAt(ts: number): void {
  try {
    localStorage.setItem(SYNC_TS_KEY, String(ts));
  } catch {
    // ignore
  }
}

export function isFavorite(article: string): boolean {
  return getFavoritesMap().has(article);
}

/**
 * Переключить избранное.
 * @returns true = добавлено, false = удалено
 */
export function toggleFavorite(article: string): boolean {
  const map = getFavoritesMap();
  const wasAdded = !map.has(article);
  if (wasAdded) {
    map.set(article, Math.floor(Date.now() / 1000));
  } else {
    map.delete(article);
  }
  saveFavorites(map);
  return wasAdded;
}

/**
 * Получить массив артикулов избранного (без timestamps).
 */
export function getFavorites(): string[] {
  return [...getFavoritesMap().keys()];
}

export function getFavoritesCount(): number {
  return getFavoritesMap().size;
}

/**
 * Отправить текущее избранное на сервер (полная замена).
 */
export function syncFavToServer(): void {
  if (!getConfig('isAuthenticated', false)) return;

  const syncUrl = getConfig('ajaxFavSyncUrl', '') as string;
  const csrfToken = getConfig('csrfToken', '') as string;

  const map = getFavoritesMap();
  const favorites: { a: string; t: number }[] = [];
  for (const [a, t] of map) {
    favorites.push({ a, t });
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  fetch(syncUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({ favorites, csrf_token: csrfToken }),
  }).catch(() => {
    showFlash('Не удалось сохранить избранное на сервере', 'error');
  });
}

/** Debounce для синхронизации (400ms) */
let _syncTimer: ReturnType<typeof setTimeout> | null = null;
export function scheduleFavSync(): void {
  if (!getConfig('isAuthenticated', false)) return;
  if (_syncTimer !== null) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(syncFavToServer, 400);
}

/**
 * Загрузить избранное с сервера.
 */
async function loadFavFromServer(): Promise<Array<{ a: string; t: number }> | null> {
  const listUrl = getConfig('ajaxFavListUrl', '') as string;
  try {
    const resp = await fetch(listUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data && data.success && Array.isArray(data.favorites)) {
      return data.favorites;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Мерж локального и серверного избранного.
 * Стратегия: «последний выигрывает» (per-item по timestamp + lastSyncAt для удалений).
 */
function mergeFavorites(
  localMap: Map<string, number>,
  serverList: Array<{ a: string; t: number }>
): Map<string, number> {
  const lastSyncAt = getLastSyncAt();
  const merged = new Map(localMap);

  const serverSet = new Set<string>();

  for (const item of serverList) {
    const a = String(item.a || '');
    const t = Number(item.t) || 0;
    if (!a) continue;
    serverSet.add(a);

    if (merged.has(a)) {
      merged.set(a, Math.max(merged.get(a)!, t));
    } else {
      if (lastSyncAt === 0 || t > lastSyncAt) {
        merged.set(a, t);
      }
    }
  }

  if (lastSyncAt > 0) {
    for (const [a, t] of merged) {
      if (!serverSet.has(a) && t <= lastSyncAt) {
        merged.delete(a);
      }
    }
  }

  return merged;
}

/**
 * Полный цикл синхронизации при инициализации (для авторизованных).
 */
export async function mergeFavoritesWithServer(): Promise<void> {
  if (!getConfig('isAuthenticated', false)) return;

  const localMap = getFavoritesMap();
  const serverList = await loadFavFromServer();

  if (serverList === null) return;

  if (serverList.length === 0 && localMap.size === 0) {
    setLastSyncAt(Math.floor(Date.now() / 1000));
    return;
  }

  const merged = mergeFavorites(localMap, serverList);

  saveFavorites(merged);
  _cache = merged;

  setLastSyncAt(Math.floor(Date.now() / 1000));

  syncFavToServer();
}
